import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiRequest } from "@/api/client";
import { Input } from "@/components/ui/input";
import { AtecoTree, CompanyFields, KindPicker } from "@/pages/SafetySurvey";
import { listSurveys, newId, putSurvey, queueAdd } from "@/lib/fieldStore";
import { emptyRecord, withRequiredTasks } from "@/lib/fieldLocal";
import { useFieldSync } from "./FieldSync";

const BIG = "min-h-[64px] rounded-2xl border-2 px-5 text-lg font-bold disabled:opacity-40";
const PRIMARY = `${BIG} border-[#0D4D4F] bg-[#0D4D4F] text-white`;
const SECOND = `${BIG} border-[#0D4D4F] bg-white text-[#0D4D4F]`;
const EMPTY = { business_name: "", vat_number: "", fiscal_code: "", legal_address: "", operating_address: "", ateco_code: "", employees_total: "", workers: [] };

// I campi dei documenti scelti che non sono gia' nei dati azienda (committente, ponteggio, preposto...), senza doppioni.
export function metaFieldsFor(catalog, kinds) {
  const seen = new Set();
  return catalog.document_catalog.filter((c) => kinds.includes(c.kind)).flatMap((c) => c.fields.filter((f) => f.scope === "meta"))
    .filter((f) => (seen.has(f.key) ? false : seen.add(f.key)));
}

// L'auditor apre da solo, sul posto, azienda e sopralluogo: nessun passaggio preliminare dal consulente.
// Funziona anche senza rete: il sopralluogo nasce sul tablet e viene aperto sul server appena c'e' la rete.
export default function FieldNew() {
  const navigate = useNavigate();
  const { pack, online, kick } = useFieldSync();
  const [mine, setMine] = useState([]);
  const [step, setStep] = useState(0);
  const [companyId, setCompanyId] = useState(null);
  const [company, setCompany] = useState(EMPTY);
  const [kinds, setKinds] = useState(["dvr"]);
  const [tasks, setTasks] = useState([]);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null); // stesso nome di un'altra azienda: decide l'auditor
  const catalog = pack ? pack.catalog : null;

  useEffect(() => {
    (async () => {
      const local = (await listSurveys()).filter((r) => r.company_id).map((r) => ({ id: r.company_id, business_name: (r.company || {}).business_name || r.title, ateco_code: (r.company || {}).ateco_code, total_workers: (r.company || {}).employees_total }));
      let remote = [];
      if (navigator.onLine !== false) { try { remote = (await apiRequest("/field/companies")).companies; } catch { remote = []; } }
      const byId = new Map();
      [...remote, ...local].forEach((c) => { if (c.id && c.business_name && !byId.has(c.id)) byId.set(c.id, c); });
      setMine([...byId.values()]);
    })();
  }, []);

  const metaFields = useMemo(() => (catalog ? metaFieldsFor(catalog, kinds) : []), [catalog, kinds]);
  if (!catalog) return <div className="rounded-2xl bg-[#FBEFD2] p-5 text-[#8A5A00]">{online ? <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Preparo l'app per lavorare anche senza rete…</span> : "Per aprire un sopralluogo senza rete l'app deve essere stata aperta almeno una volta con internet."}</div>;

  const addsTasks = catalog.document_catalog.some((c) => kinds.includes(c.kind) && c.adds_tasks);
  const steps = ["Azienda", "Documenti", "Attività", ...(metaFields.length ? ["Dati dei documenti"] : [])];
  const last = step === steps.length - 1;
  const okStep = [company.business_name.trim().length >= 2, kinds.length > 0, tasks.length > 0 || addsTasks, true][step];

  const pickMine = (c) => { setCompanyId(c.id); setCompany({ ...EMPTY, business_name: c.business_name, ateco_code: c.ateco_code || "", employees_total: c.total_workers || "" }); };

  const create = async (decision) => {
    setBusy(true);
    try {
      const id = newId();
      const cleanMeta = Object.fromEntries(Object.entries(meta).filter(([, v]) => String(v).trim()));
      const comp = { business_name: company.business_name.trim(), vat_number: company.vat_number, fiscal_code: company.fiscal_code, legal_address: company.legal_address,
        operating_address: company.operating_address, ateco_code: company.ateco_code, employees_total: company.employees_total };
      const rec = emptyRecord(id, { company_id: companyId, title: cleanMeta.site || comp.operating_address || comp.business_name, company: comp, meta: { ...cleanMeta, company_name: comp.business_name },
        document_kinds: kinds, task_codes: withRequiredTasks(catalog, pack, kinds, tasks) });
      await putSurvey(rec);
      await queueAdd({ type: "start", survey_id: id, uid: newId(), data: { company_id: companyId, company: comp, document_kinds: kinds, task_codes: tasks, meta: cleanMeta, duplicate_decision: decision || undefined } });
      kick();
      toast.success(navigator.onLine === false ? "Sopralluogo aperto sul tablet: parte appena torna la rete" : "Sopralluogo aperto: puoi iniziare");
      navigate(`/campo/${id}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const start = async () => {
    // in linea: prima si controlla che l'azienda non esista gia' (si risponde solo si'/no, mai i dati altrui)
    if (!companyId && navigator.onLine !== false) {
      setBusy(true);
      try {
        const r = await apiRequest("/field/companies/check", { method: "POST", body: JSON.stringify({ business_name: company.business_name, vat_number: company.vat_number }) });
        if (r.vat_match) toast.info("Questa azienda è già in piattaforma: il sopralluogo si collega a quella, senza doppioni.");
        else if (r.name_matches.length) { setAsk(r.name_matches); setBusy(false); return; }
      } catch { /* senza risposta si procede: il server controlla comunque */ }
      setBusy(false);
    }
    await create(null);
  };

  return (
    <div className="space-y-4" data-testid="field-new">
      <button onClick={() => navigate("/campo")} className="inline-flex items-center gap-2 text-[#0D4D4F]"><ArrowLeft className="h-5 w-5" /> I tuoi sopralluoghi</button>
      <h1 className="font-heading text-2xl font-bold">Nuovo sopralluogo</h1>
      <div className="flex flex-wrap gap-2">
        {steps.map((n, i) => <span key={n} className={`rounded-full px-3 py-1 text-sm font-bold ${i === step ? "bg-[#0D4D4F] text-white" : i < step ? "bg-[#DDF0E5] text-[#1F6B45]" : "bg-white text-[#5C6667]"}`}>{i + 1}. {n}</span>)}
      </div>

      {ask && (
        <div className="space-y-3 rounded-3xl border-2 border-[#8A5A00] bg-[#FBEFD2] p-5" data-testid="field-duplicate">
          <p className="text-lg font-bold text-[#8A5A00]">Esiste già un'azienda con lo stesso nome{ask.some((a) => a.city) ? ` (${ask.map((a) => a.city).filter(Boolean).join(", ")})` : ""}.</p>
          <p>È la stessa che stai visitando?</p>
          <div className="flex flex-wrap gap-3">
            <button className={PRIMARY} disabled={busy} onClick={() => { setAsk(null); create("same"); }}>Sì, è la stessa</button>
            <button className={SECOND} disabled={busy} onClick={() => { setAsk(null); create("new"); }}>No, è un'altra azienda</button>
          </div>
        </div>)}

      <div className="rounded-3xl border border-[#D9CBB4] bg-white p-5">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Dove sei?</h2>
            {mine.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-[#5C6667]">Aziende che hai già visitato:</p>
                {mine.map((c) => (
                  <button key={c.id} onClick={() => pickMine(c)} className={`flex min-h-[56px] w-full items-center gap-3 rounded-2xl border-2 px-4 text-left ${companyId === c.id ? "border-[#0D4D4F] bg-[#E3EEEE]" : "border-[#D9CBB4]"}`}>
                    <Building2 className="h-5 w-5 shrink-0 text-[#0D4D4F]" /><span className="font-semibold">{c.business_name}</span>
                  </button>))}
                {companyId && <button className="text-sm text-[#0D4D4F] underline" onClick={() => { setCompanyId(null); setCompany(EMPTY); }}>Un'altra azienda: inserisco i dati</button>}
              </div>)}
            <p className="text-sm text-[#5C6667]">Scrivi i dati dell'azienda che stai visitando. Basta la ragione sociale: il resto lo puoi aggiungere dopo.</p>
            <CompanyFields base="/field" company={company} setCompany={setCompany} />
          </div>)}
        {step === 1 && (
          <div className="space-y-3"><h2 className="text-xl font-bold">Quali documenti devi produrre?</h2>
            <p className="text-sm text-[#5C6667]">Puoi sceglierne più di uno: l'app prepara una bozza per ciascuno dallo stesso sopralluogo.</p>
            <KindPicker catalog={catalog} kinds={kinds} setKinds={setKinds} /></div>)}
        {step === 2 && (
          <div className="space-y-3"><h2 className="text-xl font-bold">Che attività svolge?</h2>
            <AtecoTree base="/field" catalog={catalog} tasks={tasks} setTasks={setTasks} atecoCode={company.ateco_code} />
            {addsTasks && tasks.length === 0 && <p className="text-sm text-[#5C6667]">I documenti scelti aggiungono già le loro domande: le attività le puoi scegliere anche dopo.</p>}</div>)}
        {step === 3 && metaFields.length > 0 && (
          <div className="space-y-3"><h2 className="text-xl font-bold">Dati richiesti dai documenti</h2>
            <p className="text-sm text-[#5C6667]">Quelli che non hai ora puoi lasciarli vuoti e completarli durante il sopralluogo.</p>
            {metaFields.map((f) => (
              <label key={f.key} className="block text-sm font-semibold">{f.label}
                <Input className="mt-1 h-12 border-2 text-base" value={meta[f.key] || ""} onChange={(e) => setMeta({ ...meta, [f.key]: e.target.value })} /></label>))}
          </div>)}
      </div>

      <div className="flex gap-3">
        {step > 0 && <button className={SECOND} onClick={() => setStep(step - 1)}>Indietro</button>}
        {!last
          ? <button className={PRIMARY} disabled={!okStep} onClick={() => setStep(step + 1)}>Avanti</button>
          : <button className={PRIMARY} disabled={busy || !okStep || !!ask} onClick={start} data-testid="field-start">{busy ? "Apro…" : "Inizia il sopralluogo"}</button>}
      </div>
    </div>
  );
}
